import { createApi, fakeBaseQuery, SkipToken } from "@reduxjs/toolkit/query/react";
import { db } from "@/firebase";
import { addDoc, collection, deleteDoc, doc, getDoc, getDocs, updateDoc } from "firebase/firestore";
import { addLoadingTask, deleteLoadingTask } from "@/store/uiSlice";
import { DraftMenu, Menu, NewMenu } from "@/types";
import { fetchUsers } from "@/store/usersSlice";
import pick from "lodash/pick";
import { ApiError } from "@/types/api";

const DEFAULT_TEMPLATE = "ToKMxmVapOJpdYh5k5X0";

export const menusApi = createApi({
  reducerPath: "menusApi",
  baseQuery: fakeBaseQuery<ApiError>(),
  tagTypes: ["Menu"],
  endpoints: (builder) => ({
    fetchMenus: builder.query<{ data: Menu[] }, string | SkipToken>({
      queryFn: async (organizationId, { dispatch }) => {
        const TASK = "Menús del comercios";

        if (!organizationId) {
          return {
            error: {
              status: 400,
              statusText: "Consulta inválida",
            },
          };
        }

        try {
          dispatch(addLoadingTask(TASK));
          const collectionRef = collection(db, `organizations/${String(organizationId)}/menus`);

          const response = (await getDocs(collectionRef).then((querySnapshot) => {
            return querySnapshot.docs.reduce((acc, doc: any) => {
              return [...acc, { ...doc.data(), id: doc.id }];
            }, [] as unknown as Menu[]);
          })) as Menu[];

          const authors = response.map((menu) => menu.createdBy);

          dispatch(fetchUsers(authors));

          if (!response.length) {
            return {
              error: {
                status: 404,
                statusText: "Menús no encontrados",
              },
            };
          }

          return { data: response };
        } catch {
          return {
            error: {
              status: 500,
              statusText: "Error al obtener los menús",
            },
          };
        } finally {
          dispatch(deleteLoadingTask(TASK));
        }
      },
      providesTags: ["Menu"],
    }),
    fetchMenuById: builder.query({
      queryFn: async (
        {
          organizationId,
          menuId,
        }: {
          organizationId: string | SkipToken;
          menuId: string | SkipToken;
        },
        { dispatch }
      ) => {
        const TASK = "Menú";

        try {
          dispatch(addLoadingTask(TASK));
          const docRef = doc(db, `organizations/${String(organizationId)}/menus/${String(menuId)}`);
          const docSnap = await getDoc(docRef);

          if (docSnap.exists()) {
            const menu = { ...docSnap.data(), id: menuId };
            dispatch(fetchUsers([menu.createdBy]));
            return { data: menu };
          }

          return { data: null };
        } catch (error) {
          return { error };
        } finally {
          dispatch(deleteLoadingTask(TASK));
        }
      },
      providesTags: ["Menu"],
    }),
    addMenu: builder.mutation({
      queryFn: async ({ draft, organizationId }: { draft: DraftMenu; organizationId: string }, { dispatch, getState }) => {
        const TASK = "Crear menú";

        if (!draft || !organizationId) {
          return {
            error: {
              status: 400,
              statusText: "Consulta inválida",
            },
          };
        }

        const store = getState() as any;
        try {
          dispatch(addLoadingTask(TASK));
          const collectionRef = collection(db, `organizations/${String(organizationId)}/menus`);

          const ALLOWED_FIELDS = ["createdBy", "createdAt", "description", "flow", "image", "isPublished", "template", "title", "order"];

          const payload: Partial<NewMenu> = {
            ...pick(draft, ALLOWED_FIELDS),
            createdBy: store.auth.user.uid,
            createdAt: Date.now(),
            template: DEFAULT_TEMPLATE,
          };

          const doc = await addDoc(collectionRef, payload);

          return {
            data: {
              ...payload,
              id: doc.id,
            },
          };
        } catch (error) {
          return {
            error: {
              status: 500,
              statusText: "Error al crear el menú",
            },
          };
        } finally {
          dispatch(deleteLoadingTask(TASK));
        }
      },
      invalidatesTags: ["Menu"],
    }),
    deleteMenu: builder.mutation({
      queryFn: async ({ menuId, organizationId }: { menuId: string; organizationId: string }) => {
        try {
          const collectionRef = doc(db, `organizations/${organizationId}/menus/${menuId}`);
          const response = await deleteDoc(collectionRef);
          return { data: response };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: ["Menu"],
    }),
    updateMenu: builder.mutation({
      queryFn: async ({ menu, organizationId }: { menu: Menu; organizationId: string }, { getState }) => {
        const store: any = getState();

        try {
          if (!store.auth?.user?.uid || !menu) return { data: null };
          const docRef = doc(db, `organizations/${organizationId}/menus/${menu.id}`);

          const ALLOWED_FIELDS = ["title", "description", "image", "isPrivate", "isPublished", "template", "flow", "structure", "order"];

          const payload = {
            ...pick(menu, ALLOWED_FIELDS),
            lastUpdatedBy: store.auth.user.uid,
            lastUpdatedAt: Date.now(),
          };

          const response = await updateDoc(docRef, payload);
          return { data: response };
        } catch (error) {
          console.error(error);
          throw error;
        }
      },
      invalidatesTags: ["Menu"],
    }),
    duplicateMenu: builder.mutation({
      queryFn: async ({ menu, organizationId }: { menu: Menu; organizationId: string }, { dispatch, getState }) => {
        const TASK = "Crear copia del menú";

        const store = getState() as any;
        try {
          if (!store.auth?.user?.uid || !menu) return { data: null };

          dispatch(addLoadingTask(TASK));
          const collectionRef = collection(db, `organizations/${String(organizationId)}/menus`);

          const ALLOWED_FIELDS = ["title", "description", "image", "isPrivate", "isPublished", "template", "flow", "structure"];

          const payload: NewMenu = {
            ...pick(menu, ALLOWED_FIELDS),
            createdBy: store.auth.user.uid,
            createdAt: Date.now(),
          };

          const response = await addDoc(collectionRef, payload);
          return { data: response };
        } catch (error) {
          console.error(error);
          throw error;
        } finally {
          dispatch(deleteLoadingTask(TASK));
        }
      },
      invalidatesTags: ["Menu"],
    }),
  }),
});

export const { useFetchMenusQuery, useLazyFetchMenusQuery, useFetchMenuByIdQuery, useAddMenuMutation, useDeleteMenuMutation, useUpdateMenuMutation, useDuplicateMenuMutation } =
  menusApi as any;
